import { Gallery } from 'iconsax-react';
import { isNil, pickBy } from 'lodash-es';
import type { ChangeEvent, PropsWithChildren } from 'react';
import usePickMediaFile from '@/hooks/files/use-pick-media-file';
import useUploadFile from '@/hooks/files/use-upload-file';
import useUpdateGroupDetails from '@/hooks/groups/use-update-group-details';
import useUserPlatform, { useUserHints } from '@/hooks/use-user-platform';
import { FileType } from '@/utils/file-type';
import { convertFileToUploadable } from '@/utils/uploadable';

interface Props {
  showIcon?: boolean;
  canManage?: boolean;
  groupId: number;
}

export function GroupAvatarUploadButton({ canManage, showIcon = true, groupId, children }: PropsWithChildren<Props>) {
  const { isDesktop } = useUserPlatform();
  const {
    hints: { osType },
  } = useUserHints();
  const { ref: inputRef, promptPickMedia, pickMedia, clearMedia } = usePickMediaFile();
  const { upload, isLoading: isUploading } = useUploadFile();
  const { mutateAsync: updateGroup, isPending: isUpdating } = useUpdateGroupDetails(groupId);

  const handleSelectFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      const avatarUrl = await upload(convertFileToUploadable(file), 'avatar');
      await updateGroup(
        pickBy(
          {
            GroupImg: avatarUrl,
          },
          (v) => !isNil(v),
        ),
      );

      clearMedia();
    }
  };

  if (!canManage) return children;

  return (
    <button
      type='button'
      disabled={isUploading || isUpdating}
      className='group relative'
      onClick={() => {
        isDesktop || osType === 'desktop' ? pickMedia({ type: 'album', accept: FileType.Image }) : promptPickMedia();
      }}>
      {showIcon && (
        <div className='absolute bottom-0 right-0 flex size-6 items-center justify-center rounded-full bg-white p-1 shadow-lg z-20'>
          <div className='group-disabled:blur'>
            <Gallery className='size-4 text-secondary' />
          </div>

          <span className='loading loading-spinner loading-sm absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 text-secondary group-disabled:inline' />
        </div>
      )}

      <div className='relative'>
        {children}

        <input
          type='file'
          className='sr-only'
          accept='image/*'
          ref={inputRef}
          value=''
          onChange={handleSelectFile}
        />
      </div>
    </button>
  );
}
